import { Link } from "react-router-dom";
import type { PublicRelease } from "../api/types";
import { rarityAccent, confirmedRarities } from "../catalog/rarity";
import { useCollection } from "../collection/CollectionContext";
import { useGridDensity } from "../ui/GridDensityContext";
import { CollectionQuantityControls } from "./CollectionQuantityControls";
import { RarityBadge } from "./RarityBadge";
import { ReleaseImage } from "./ReleaseImage";
import { formatReleaseCollector, ReleaseMeta } from "./ReleaseMeta";

type ReleaseCardProps = {
  release: PublicRelease;
  priorityImage?: boolean;
  variant?: "catalog" | "collection";
};

export function ReleaseCard({
  release,
  priorityImage = false,
  variant = "catalog",
}: ReleaseCardProps) {
  const collection = useCollection();
  const { density } = useGridDensity();
  const quantity = collection.getQuantity(release.releaseId);
  const busy =
    collection.mutatingReleaseId !== null &&
    collection.mutatingReleaseId !== release.releaseId;
  const mutatingThis = collection.mutatingReleaseId === release.releaseId;
  const accent = rarityAccent(release);
  const rarities = confirmedRarities(release);
  const owned = quantity > 0;
  const collector = formatReleaseCollector(release);
  const cardClass = [
    "hw-release-card",
    `hw-release-card--${variant}`,
    `hw-release-card--d${density}`,
    accent ? `hw-release-card--${accent.toLowerCase()}` : "",
    owned ? "hw-release-card--owned" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const controlsDisabled =
    busy ||
    mutatingThis ||
    collection.mutationsDisabled ||
    collection.mutatingReleaseId === "__import__";

  return (
    <article className={cardClass}>
      <Link
        to={`/releases/${release.releaseId}`}
        className="hw-release-card__media"
        aria-label={`Подробнее: ${release.casting.name}`}
      >
        <ReleaseImage
          image={release.primaryImage}
          alt={release.casting.name}
          priority={priorityImage}
        />
        {collector || release.packName ? (
          <span className="hw-release-card__number" title={collector || "5-Pack"}>
            {collector || "5-Pack"}
          </span>
        ) : null}
        {rarities.length > 0 ? (
          <span className="hw-release-card__badges">
            {rarities.map((rarity) => (
              <RarityBadge key={rarity} rarity={rarity} />
            ))}
          </span>
        ) : null}
      </Link>
      <div className="hw-release-card__body">
        <Link
          to={`/releases/${release.releaseId}`}
          className="hw-release-card__link"
        >
          <ReleaseMeta
            release={release}
            headingLevel="h2"
            compact
            density={density}
          />
        </Link>
        <div className="hw-release-card__actions">
          <CollectionQuantityControls
            quantity={quantity}
            compact
            quiet={density !== 1}
            micro={density === 3}
            disabled={controlsDisabled}
            onAdd={() => {
              void collection.setQuantity(release.releaseId, 1);
            }}
            onIncrement={() => {
              void collection.setQuantity(release.releaseId, quantity + 1);
            }}
            onDecrement={() => {
              void collection.setQuantity(release.releaseId, quantity - 1);
            }}
            onRemove={() => {
              void collection.setQuantity(release.releaseId, 0);
            }}
          />
        </div>
      </div>
    </article>
  );
}
